import React, { useContext, useState } from 'react';
import { LocaleFlag } from './LocaleFlag';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import useAsyncEffect from '../common/react-hooks/useAsyncEffect';
import { FuseboxTranslationTable } from './fusebox-editor/FuseboxTranslationTable';
import MultiButtonSwitch from '../common/editors/MultiButtonSwitch';
import { IconButton } from '../common/IconButton';
import { Icon } from '../common/Icon';

const defaultOptions = ['es', 'en', 'pt', 'it', 'fr', 'de'];

const ellipsis = (s, n = 120) => (s || '').slice(0, n) + (s?.length > n ? '...' : '');

function FuseboxTranslationModalContent({ lego, translationDoc, locale }) {
  const stringTranslations = translationDoc.data?.translations || {};

  const displayOriginalBoxes = lego.data.map(box => ({
    ...box,
    fuses: box.fuses.map(f => ({
      ...f,
      description: (box.useAcronyms && f.extendedDescription) ? `(${f.description}) ${f.extendedDescription}` : f.description,
    })),
  }));

  const translatedBoxes = lego.data.map((box) => {
    const boxName = stringTranslations[box.boxName]?.[locale] || '-';
    const boxDescription = stringTranslations[box.boxDescription]?.[locale] || '-';
    const fuses = box.fuses.map(f => {
      let lookupKey = f.description;
      if (box.useAcronyms && f.extendedDescription) {
        lookupKey = `(${f.description}) ${f.extendedDescription}`;
      }
      const transDesc = lookupKey ? (stringTranslations[lookupKey]?.[locale] || '-') : f.description;
      return { description: transDesc };
    });
    return { boxName, boxDescription, fuses };
  });

  return <FuseboxTranslationTable originalBoxes={displayOriginalBoxes} translatedBoxes={translatedBoxes} />;
}

function ArticleTranslationModalContent({ lego, translationDoc, locale }) {
  const data = translationDoc.data || {};
  const original = lego.data || {};

  const fields = [
    { label: 'Title', original: original.title, translated: data.title?.[locale] },
    { label: 'Topic Short', original: original.articleTopicShort, translated: data.articleTopicShort?.[locale] },
    { label: 'Content', original: original.content, translated: data.content?.[locale] },
  ];

  return (
    <table className="table table-sm table-bordered mt-2">
      <thead>
        <tr>
          <th>Field</th>
          <th>Original</th>
          <th className="bg-light-success">Translated</th>
        </tr>
      </thead>
      <tbody>
        {fields.map(({ label, original: orig, translated }) => (
          <tr key={label}>
            <td className="font-weight-bold" style={{ width: '100px' }}>{label}</td>
            <td className="text-secondary small">{label === 'Content' ? <div dangerouslySetInnerHTML={{ __html: ellipsis(orig, 5000) }} /> : ellipsis(orig)}</td>
            <td className="small bg-light-success">{translated
              ? (label === 'Content' ? <div dangerouslySetInnerHTML={{ __html: ellipsis(translated, 5000) }} /> : ellipsis(translated))
              : <span className="text-muted">-</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ShowTranslationModal({ legoId, legoType, locale }) {
  const { page } = useContext(LegoAdminPageContext);
  const [lego, setLego] = useState(null);
  const [translationDoc, setTranslationDoc] = useState(null);

  useAsyncEffect(async () => {
    if (!legoId || !page) return;

    const [originalLego, translation] = await Promise.all([
      page.service('services/legos').get(legoId),
      page.service('services/legos/translations').get(legoId),
    ]);

    setLego(originalLego);
    setTranslationDoc(translation);
  }, [legoId, page], []);

  if (!lego || !translationDoc) return <div className="p-3 text-center">Loading...</div>;

  const effectiveType = legoType || lego.type;

  return (
    <div className="p-3">
      <h4 className="text-center">Translation to {locale} <LocaleFlag locale={locale} /></h4>
      {effectiveType === 'article'
        ? <ArticleTranslationModalContent lego={lego} translationDoc={translationDoc} locale={locale} />
        : <FuseboxTranslationModalContent lego={lego} translationDoc={translationDoc} locale={locale} />}
    </div>
  );
}


export function LangTranslationSelector({ value, onChange, legoId, legoType }) {
  const { page } = useContext(LegoAdminPageContext);
  const [translatedLocales, setTranslatedLocales] = useState(legoId ? null : []);
  const [expanded, setExpanded] = useState(false);

  useAsyncEffect(async () => {
    if (!legoId || !page) return;

    const translation = await page.service('services/legos/translations').get(legoId);
    const data = Array.isArray(translation?.data) ? translation.data[0] : translation?.data;

    setTranslatedLocales(data?.langs || []);
  }, [legoId, page], []);

  const showTranslationModal = (newLocale) => {    
    page.openModal(<ShowTranslationModal legoId={legoId} legoType={legoType} locale={newLocale} />);    
  };

  const options = expanded ? defaultOptions : [value];

  return <span className='zoom-90 mr-3' title="Automatic translations and current lego language">
    <Icon small icon={'translate'} level={'secondary'} className='mr-2 ml-2' />

    { expanded 
    ?
    <MultiButtonSwitch label={'Lang'} className='ml-3' value={value} onChange={onChange} options={options.map(locale => ({ value: locale, label: <LocaleFlag locale={locale} /> }))} />
     : 
     <span className='mr-1'><LocaleFlag locale={value} /></span>
     } 

    
    { !translatedLocales ? '...' : null}

    {translatedLocales?.map(locale => <span key={locale} style={{borderRadius: '15px', padding: '1px 4px'}} className='btn btn-success bg-light-success btn-sm mr-1' onClick={() => showTranslationModal(locale)}>
      <LocaleFlag locale={locale} />
    </span>)}
    
    <IconButton icon={expanded ? 'close' : 'edit'} level={expanded ? 'secondary' : 'primary'} className='' onClick={() => setExpanded(!expanded)} />
  </span>
}
